import { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { API, AuthContext } from '@/App';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Search, Plus, Trash2, ShoppingCart, User, Upload, CheckCircle } from 'lucide-react';
import { formatCurrency, fmtNum } from '@/utils/currency';
import CurrencySymbol from '@/components/CurrencySymbol';

export default function POS() {
  const { token } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [tax, setTax] = useState(10);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [loading, setLoading] = useState(false);
  const [transferSlip, setTransferSlip] = useState(null);
  const [transferSlipPreview, setTransferSlipPreview] = useState(null);
  const [currentOrderId, setCurrentOrderId] = useState(null);
  const orderLoadedRef = useRef(false);
  
  // Payment dialog states
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentStep, setPaymentStep] = useState(1);
  const [paymentData, setPaymentData] = useState({
    currency: '',
    bank_id: '',
    payment_method: 'transfer',
    payment_slip: null
  });
  const [paymentSlipPreview, setPaymentSlipPreview] = useState(null);
  const [banks, setBanks] = useState([]);
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [exchangeRates, setExchangeRates] = useState([]);

  useEffect(() => {
    fetchProducts();
    fetchCustomers();
    fetchBanks();
    fetchExchangeRates();
  }, []);

  // Reset the order loaded flag when location state changes
  useEffect(() => {
    if (location.state?.orderData) {
      orderLoadedRef.current = false;
    }
  }, [location.state?.orderData]);

  // Separate effect for loading order data after customers are fetched
  useEffect(() => {
    if (location.state?.orderData && customers.length > 0 && !orderLoadedRef.current) {
      orderLoadedRef.current = true;
      loadOrderIntoCart(location.state.orderData);
    }
  }, [location.state, customers]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to load products');
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      toast.error('Failed to load customers');
    }
  };

  const fetchBanks = async () => {
    try {
      const response = await axios.get(`${API}/settings/invoice-quotation`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBanks(response.data.payment_banks || []);
      
      // Get unique currencies from all banks
      const currencies = new Set();
      response.data.payment_banks?.forEach(bank => {
        bank.accounts?.forEach(account => {
          if (account.account_number) {
            currencies.add(account.currency);
          }
        });
      });
      setAvailableCurrencies(Array.from(currencies));
    } catch (error) {
      console.error('Failed to fetch banks');
    }
  };

  const fetchExchangeRates = async () => {
    try {
      const response = await axios.get(`${API}/settings/currency`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExchangeRates(response.data.exchange_rates || []);
    } catch (error) {
      console.error('Failed to fetch exchange rates');
    }
  };

  const getConvertedAmount = () => {
    const { total } = calculateTotals();
    
    if (!paymentData.currency || paymentData.currency === 'USD') {
      return total;
    }
    
    const rate = exchangeRates.find(r => r.currency === paymentData.currency);
    if (rate) {
      return total * rate.rate_to_usd;
    }
    
    return total;
  };


  const loadOrderIntoCart = async (orderData) => {
    try {
      console.log('Loading order into cart:', orderData);
      
      // Load order items into cart - match the structure used by addToCart
      const cartItems = orderData.items.map(item => ({
        product_id: item.product_id,
        product_name: item.product_name,
        quantity: item.quantity,
        price: item.price,
        discount: 0,
        total: item.total || (item.price * item.quantity)
      }));
      
      console.log('Cart items to load:', cartItems);
      setCart(cartItems);
      setCurrentOrderId(orderData.id);
      
      // Try to find or create customer
      const existingCustomer = customers.find(c => 
        c.phone === orderData.customer_phone || c.name === orderData.customer_name
      );
      
      if (existingCustomer) {
        setSelectedCustomer(existingCustomer.id);
        console.log('Found existing customer:', existingCustomer.name);
      } else if (orderData.customer_name && orderData.customer_phone) {
        // Create new customer
        try {
          console.log('Creating new customer:', orderData.customer_name);
          const newCustomer = await axios.post(
            `${API}/customers`,
            {
              name: orderData.customer_name,
              email: orderData.customer_email || '',
              phone: orderData.customer_phone,
              address: ''
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setSelectedCustomer(newCustomer.data.id);
          fetchCustomers(); // Refresh customer list
          console.log('New customer created:', newCustomer.data);
        } catch (error) {
          console.error('Failed to create customer:', error);
          toast.error('Failed to create customer, but order items loaded');
        }
      }
      
      toast.success(`Order ${orderData.order_number} loaded into POS`);
      console.log('Order loaded successfully. Cart state:', cartItems);
    } catch (error) {
      console.error('Error loading order:', error);
      toast.error('Failed to load order into cart');
    }
  };

  const searchProducts = async () => {
    if (!searchQuery.trim()) {
      fetchProducts();
      return;
    }

    try {
      const response = await axios.get(`${API}/products/search/${searchQuery}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      toast.error('Search failed');
    }
  };

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.product_id === product.id);
    
    if (existingItem) {
      setCart(cart.map(item =>
        item.product_id === product.id
          ? { ...item, quantity: item.quantity + 1, total: (item.quantity + 1) * item.price }
          : item
      ));
    } else {
      setCart([...cart, {
        product_id: product.id,
        product_name: product.name,
        quantity: 1,
        price: product.price,
        discount: 0,
        total: product.price
      }]);
    }
    toast.success(`${product.name} added to cart`);
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }
    
    setCart(cart.map(item =>
      item.product_id === productId
        ? { ...item, quantity: newQuantity, total: newQuantity * item.price }
        : item
    ));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.product_id !== productId));
  };

  const calculateTotals = () => {
    const subtotal = cart.reduce((sum, item) => sum + item.total, 0);
    const discountAmount = (subtotal * discount) / 100;
    const taxAmount = ((subtotal - discountAmount) * tax) / 100;
    const total = subtotal - discountAmount + taxAmount;
    
    return { subtotal, discountAmount, taxAmount, total };
  };

  const handleTransferSlipUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      setTransferSlip(reader.result);
      setTransferSlipPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const completeSale = async () => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }

    // Open payment dialog
    setPaymentStep(1);
    setPaymentData({
      currency: '',
      bank_id: '',
      payment_method: 'transfer',
      payment_slip: null
    });
    setPaymentSlipPreview(null);
    setShowPaymentDialog(true);
  };

  const handlePaymentSlipUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPaymentData({ ...paymentData, payment_slip: reader.result });
      setPaymentSlipPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const completePayment = async () => {
    setLoading(true);
    const { subtotal, discountAmount, taxAmount, total } = calculateTotals();

    try {
      // Calculate amount in payment currency
      let amountInPaymentCurrency = total;
      if (paymentData.currency !== 'USD') {
        const rate = exchangeRates.find(r => r.currency === paymentData.currency);
        if (rate) {
          amountInPaymentCurrency = total * rate.rate_to_usd;
        }
      }

      // Complete the sale
      const saleResponse = await axios.post(`${API}/sales`, {
        customer_id: selectedCustomer,
        customer_name: customers.find(c => c.id === selectedCustomer)?.name || 'Walk-in Customer',
        items: cart,
        subtotal,
        discount: discountAmount,
        tax: taxAmount,
        total,
        payment_method: 'transfer',
        transfer_slip: paymentData.payment_slip
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Record payment in accounting with converted amount
      await axios.post(`${API}/accounting/record-payment`, {
        reference_id: saleResponse.data.sale_id || 'POS-' + Date.now(),
        reference_type: 'pos',
        payment_currency: paymentData.currency,
        payment_method: paymentData.payment_method,
        bank_id: paymentData.bank_id,
        amount: amountInPaymentCurrency,
        payment_slip: paymentData.payment_slip
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Sale completed and payment recorded!');
      
      // If this sale came from an order, mark the order as completed
      if (currentOrderId) {
        try {
          await axios.put(
            `${API}/orders/${currentOrderId}/status`,
            { status: 'completed' },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (error) {
          console.error('Failed to update order status:', error);
        }
      }
      
      setCart([]);
      setSelectedCustomer(null);
      setDiscount(0);
      setTransferSlip(null);
      setTransferSlipPreview(null);
      setCurrentOrderId(null);
      setShowPaymentDialog(false);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete sale');
    } finally {
      setLoading(false);
    }
  };

  const totals = calculateTotals();

  return (
    <Layout>
      <div className="h-full p-4 md:p-6 lg:p-8" data-testid="pos-page">
        <div className="max-w-7xl mx-auto h-full">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4 md:mb-6">Point of Sale</h1>

          <div className="grid lg:grid-cols-3 gap-4 md:gap-6">
            {/* Products Section */}
            <div className="lg:col-span-2 space-y-4 mb-6 lg:mb-0">
              <Card className="p-3 md:p-4">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-slate-400" />
                    <Input
                      placeholder="Search products..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && searchProducts()}
                      className="pl-9 md:pl-10 text-sm md:text-base h-9 md:h-10"
                      data-testid="product-search-input"
                    />
                  </div>
                  <Button onClick={searchProducts} data-testid="search-button" size="sm" className="h-9 md:h-10">
                    <Search className="w-4 h-4 md:hidden" />
                    <span className="hidden md:inline">Search</span>
                  </Button>
                </div>
              </Card>

              <div className="max-h-[500px] md:max-h-[600px] overflow-y-auto">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                  {products.map((product) => (
                    <Card
                      key={product.id}
                      className="p-2 md:p-4 cursor-pointer hover:shadow-lg transition-all border-2 border-transparent hover:border-emerald-500 active:border-emerald-600"
                      onClick={() => addToCart(product)}
                      data-testid={`product-card-${product.sku}`}
                    >
                      <div className="text-center space-y-1 md:space-y-2">
                        <div className="w-full h-16 md:h-24 bg-slate-100 rounded-lg flex items-center justify-center mb-1 md:mb-2">
                          {product.images && product.images.length > 0 ? (
                            <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover rounded-lg" />
                          ) : (
                            <Package className="w-8 h-8 md:w-12 md:h-12 text-slate-400" />
                          )}
                        </div>
                        <h3 className="font-semibold text-slate-900 text-xs md:text-sm truncate">{product.name}</h3>
                        <p className="text-[10px] md:text-xs text-slate-600">{product.sku}</p>
                        <p className="text-sm md:text-lg font-bold text-emerald-600">{formatCurrency(product.price)}</p>
                        <p className="text-[10px] md:text-xs text-slate-500">Stock: {product.quantity}</p>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </div>

            {/* Cart Section */}
            <div className="space-y-4">
              <Card className="p-3 md:p-4">
                <div className="space-y-2 md:space-y-3">
                  <Label className="text-sm md:text-base">Customer (Optional)</Label>
                  <Select value={selectedCustomer || 'walk-in'} onValueChange={(val) => setSelectedCustomer(val === 'walk-in' ? null : val)}>
                    <SelectTrigger data-testid="customer-select" className="h-9 md:h-10 text-sm md:text-base">
                      <SelectValue placeholder="Walk-in Customer" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="walk-in">Walk-in Customer</SelectItem>
                      {customers.map((customer) => (
                        <SelectItem key={customer.id} value={customer.id}>
                          {customer.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </Card>

              <Card className="p-3 md:p-4">
                <div className="flex items-center gap-2 mb-3 md:mb-4">
                  <ShoppingCart className="w-4 h-4 md:w-5 md:h-5 text-emerald-600" />
                  <h2 className="text-base md:text-lg font-bold text-slate-900">Cart ({cart.length})</h2>
                </div>

                <div className="max-h-[300px] md:max-h-[400px] overflow-y-auto space-y-2 mb-3 md:mb-4">
                  {cart.length === 0 ? (
                    <div className="text-center text-slate-500 py-6 md:py-8">
                      <ShoppingCart className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-2 opacity-50" />
                      <p className="text-sm md:text-base">Cart is empty</p>
                    </div>
                  ) : (
                    cart.map((item) => (
                      <div key={item.product_id} className="p-2 md:p-3 bg-slate-50 rounded-lg space-y-2">
                        <div className="flex justify-between items-start">
                          <div className="flex-1 pr-2">
                            <p className="font-semibold text-xs md:text-sm text-slate-900 truncate">{item.product_name}</p>
                            <p className="text-[10px] md:text-xs text-slate-600">{formatCurrency(item.price)} each</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFromCart(item.product_id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
                          >
                            <Trash2 className="w-3 h-3 md:w-4 md:h-4" />
                          </Button>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1 md:gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                              className="h-7 w-7 md:h-8 md:w-8 p-0 text-sm"
                            >
                              -
                            </Button>
                            <span className="w-6 md:w-8 text-center font-medium text-xs md:text-sm">{item.quantity}</span>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                              className="h-7 w-7 md:h-8 md:w-8 p-0 text-sm"
                            >
                              +
                            </Button>
                          </div>
                          <p className="font-bold text-slate-900 text-sm md:text-base">{formatCurrency(item.total)}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="space-y-2 md:space-y-3 pt-3 md:pt-4 border-t border-slate-200">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px] md:text-xs">Discount %</Label>
                      <Input
                        type="number"
                        value={discount}
                        onChange={(e) => setDiscount(Number(e.target.value))}
                        className="h-8 md:h-9 text-sm"
                        min="0"
                        max="100"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] md:text-xs">Tax %</Label>
                      <Input
                        type="number"
                        value={tax}
                        onChange={(e) => setTax(Number(e.target.value))}
                        className="h-8 md:h-9 text-sm"
                        min="0"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-[10px] md:text-xs">Payment Method</Label>
                    <Select value={paymentMethod} onValueChange={(value) => {
                      setPaymentMethod(value);
                      if (value !== 'bank_transfer') {
                        setTransferSlip(null);
                        setTransferSlipPreview(null);
                      }
                    }}>
                      <SelectTrigger className="h-8 md:h-9 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="card">Card</SelectItem>
                        <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Transfer Slip Upload */}
                  {paymentMethod === 'bank_transfer' && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <Label className="text-[10px] md:text-xs text-blue-900 mb-2 block">Upload Transfer Slip *</Label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleTransferSlipUpload}
                        className="hidden"
                        id="transfer-slip-upload"
                      />
                      <label htmlFor="transfer-slip-upload">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full text-xs"
                          onClick={() => document.getElementById('transfer-slip-upload').click()}
                        >
                          {transferSlipPreview ? 'Change Slip' : 'Upload Slip'}
                        </Button>
                      </label>
                      {transferSlipPreview && (
                        <div className="mt-2">
                          <img src={transferSlipPreview} alt="Transfer Slip" className="w-full h-24 object-cover rounded border border-blue-300" />
                          <p className="text-xs text-green-600 mt-1">✓ Slip uploaded</p>
                        </div>
                      )}
                      {!transferSlipPreview && (
                        <p className="text-xs text-red-600 mt-1">Required for bank transfer</p>
                      )}
                    </div>
                  )}

                  <div className="space-y-1 text-xs md:text-sm">
                    <div className="flex justify-between text-slate-600">
                      <span>Subtotal:</span>
                      <span>{formatCurrency(totals.subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-slate-600">
                      <span>Discount:</span>
                      <span>-{formatCurrency(totals.discountAmount)}</span>
                    </div>
                    <div className="flex justify-between text-slate-600">
                      <span>Tax:</span>
                      <span>+{formatCurrency(totals.taxAmount)}</span>
                    </div>
                    <div className="flex justify-between text-base md:text-lg font-bold text-slate-900 pt-2 border-t border-slate-200">
                      <span>Total:</span>
                      <span>{formatCurrency(totals.total)}</span>
                    </div>
                  </div>

                  <Button
                    onClick={completeSale}
                    disabled={loading || cart.length === 0}
                    className="w-full h-10 md:h-12 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold text-sm md:text-base"
                    data-testid="complete-sale-button"
                  >
                    {loading ? 'Processing...' : 'Complete Sale'}
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </div>

        {/* Payment Dialog */}
        <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {paymentStep === 1 && 'Select Payment Currency'}
                {paymentStep === 2 && 'Select Bank to Credit'}
                {paymentStep === 3 && 'Complete Payment'}
              </DialogTitle>
            </DialogHeader>

            {/* Step 1: Currency Selection */}
            {paymentStep === 1 && (
              <div className="space-y-4">
                <p className="text-sm text-slate-600">Choose the currency customer paid in:</p>
                <div className="grid grid-cols-3 gap-3">
                  {availableCurrencies.map((currency) => (
                    <Button
                      key={currency}
                      variant={paymentData.currency === currency ? "default" : "outline"}
                      className={paymentData.currency === currency ? "bg-emerald-600" : ""}
                      onClick={() => {
                        setPaymentData({ ...paymentData, currency });
                        setPaymentStep(2);
                      }}
                    >
                      {currency}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Bank Selection */}
            {paymentStep === 2 && (
              <div className="space-y-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPaymentStep(1)}
                  className="mb-2"
                >
                  ← Back
                </Button>
                <p className="text-sm text-slate-600">
                  Selected Currency: <strong>{paymentData.currency}</strong>
                </p>
                <p className="text-sm text-slate-600">Select bank to credit:</p>
                <div className="grid grid-cols-2 gap-3">
                  {banks.filter(bank => 
                    bank.accounts.some(acc => acc.currency === paymentData.currency && acc.account_number)
                  ).map((bank) => (
                    <Button
                      key={bank.id}
                      variant="outline"
                      className={`h-auto py-4 ${paymentData.bank_id === bank.id ? 'border-emerald-600 bg-emerald-50' : ''}`}
                      onClick={() => {
                        setPaymentData({ ...paymentData, bank_id: bank.id });
                        setPaymentStep(3);
                      }}
                    >
                      {bank.bank_logo && (
                        <img 
                          src={bank.bank_logo} 
                          alt={bank.bank_name} 
                          className="h-12 w-auto object-contain"
                        />
                      )}
                      {!bank.bank_logo && <span>{bank.bank_name}</span>}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 3: Payment Slip Upload */}
            {paymentStep === 3 && (
              <div className="space-y-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPaymentStep(2)}
                  className="mb-2"
                >
                  ← Back
                </Button>
                <div className="text-sm text-slate-600 space-y-1">
                  <p>Currency: <strong>{paymentData.currency}</strong></p>
                  <p>Sale Amount: <strong>${fmtNum(calculateTotals().total)} USD</strong></p>
                  {paymentData.currency !== 'USD' && (
                    <p className="text-emerald-600 font-semibold">
                      Amount Paid: <strong className="inline-flex items-center">
                        <CurrencySymbol currency={paymentData.currency} />
                        {fmtNum(getConvertedAmount())} {paymentData.currency}
                      </strong>
                    </p>
                  )}
                </div>
                
                <div>
                  <Label>Upload Payment Slip (Optional)</Label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePaymentSlipUpload}
                    className="hidden"
                    id="pos-payment-slip-upload"
                  />
                  <label htmlFor="pos-payment-slip-upload">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={() => document.getElementById('pos-payment-slip-upload').click()}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {paymentSlipPreview ? 'Change Slip' : 'Upload Slip'}
                    </Button>
                  </label>
                  {paymentSlipPreview && (
                    <div className="mt-2">
                      <img 
                        src={paymentSlipPreview} 
                        alt="Payment Slip" 
                        className="w-full h-32 object-cover rounded border"
                      />
                    </div>
                  )}
                </div>

                <Button
                  onClick={completePayment}
                  disabled={loading}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {loading ? 'Processing...' : 'Complete Sale & Record Payment'}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

const Package = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
  </svg>
);